import Action from '../Action';
import getLogger from '../logger';
import entityManager from '../entityManager';
import { PlayerControlled, WantsToAct, HexPosition } from '../../components';
import { GameEvents } from '../GameEvents';

const logger = getLogger('SolutionPlayer');

export default class SolutionPlayer {
  /**
   * @param {import('../eventBus').EventBus} eventBus
   */
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.solutions = [];
    this.selectedSolutionIndex = -1;
    this.currentStep = 0;
  }

  /**
   * Loads solutions from a solution data object.
   * @param {object | null} solutionData - The parsed content of a .solution.json file.
   */
  loadSolutions(solutionData) {
    this.solutions = [];
    this.selectedSolutionIndex = -1;
    this.currentStep = 0;

    if (solutionData && solutionData.solutions) {
      this.solutions = solutionData.solutions;
      logger.log(`Loaded ${this.solutions.length} solutions.`);
    } else {
      // It's normal for a level not to have solutions.
      this.solutions = [];
    }
  }

  /**
   * @returns {number} The number of available solutions.
   */
  getSolutionCount() {
    return this.solutions.length;
  }

  /**
   * Selects a solution to be played.
   * @param {number} index - The index of the solution to select.
   */
  selectSolution(index) {
    if (index >= 0 && index < this.solutions.length) {
      this.selectedSolutionIndex = index;
      this.currentStep = 0;
      logger.log(`Selected solution ${index + 1}`);
    } else {
      logger.error(`Invalid solution index: ${index}`);
    }
  }

  /**
   * Plays the next step of the currently selected solution.
   */
  playNextStep() {
    if (this.selectedSolutionIndex === -1) {
      logger.warn('No solution selected.');
      return;
    }

    const currentPath = this.solutions[this.selectedSolutionIndex];
    if (this.currentStep >= currentPath.length) {
      logger.log('End of solution path reached.');
      return;
    }

    const player = entityManager.query([PlayerControlled, WantsToAct])[0];
    if (!player) {
      // This can happen if the player's turn is not active yet, but the UI was clicked.
      logger.warn('SolutionPlayer: playNextStep called but no acting player found.');
      return;
    }

    const actionData = currentPath[this.currentStep];
    // Rehydrate the action data, attaching the player entity.
    // Solutions are stored with relative coordinates, so we convert back to absolute
    // using the player's current position.
    const playerPos = player.getComponent(HexPosition).coords;
    const action = Action.fromRelativeJSON(actionData, player, playerPos);

    logger.log(`Playing step ${this.currentStep + 1}: ${action.type}`, action.details);
    this.eventBus.emit(GameEvents.PLAYER_ACTION_REQUESTED, action);
    this.currentStep++;
  }
}
